import { useEffect, useState, useCallback } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Modal, Field, Select, Button, StatusBadge } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, Trash, UserCircle, DownloadSimple, Database, FileCsv, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const empty = { email: "", password: "", name: "", role: "staff" };
const roleLabel = { owner: "Owner", admin: "Admin", staff: "Staff" };
const collections = ["customers", "invoices", "email_logs", "workers", "tasks", "reminders"];

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [notif, setNotif] = useState({ configured: false, sender: null });
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => {
    load();
    api.get("/notifications/status").then(({ data }) => setNotif(data)).catch(() => {});
  }, [load]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/test", { email: testEmail || undefined });
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Account created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create account");
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove account");
    }
  };

  const download = (path) => {
    const a = document.createElement("a");
    a.href = `${API}${path}`;
    a.click();
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Team accounts & data backup" />
      <div className="p-8 space-y-8 max-w-4xl">
        {/* Users */}
        <section className="card-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-heading font-extrabold text-xl text-[#16205A]">Team Accounts</h3>
              <p className="text-sm text-slate-500 mt-1">People who can log in. New members sign in with the email &amp; password you set here.</p>
            </div>
            <Button data-testid="add-user-button" onClick={() => setOpen(true)}>
              <Plus size={16} weight="bold" /> Add Account
            </Button>
          </div>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-[#16205A]/5 flex items-center justify-center">
                    <UserCircle size={24} weight="fill" className="text-[#16205A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-800">
                      {u.name} {u.id === user?.id && <span className="text-slate-400 font-normal">(you)</span>}
                    </p>
                    <p className="font-mono text-xs text-slate-400">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={u.role === "owner" ? "paid" : u.role === "admin" ? "pending" : "todo"} label={roleLabel[u.role] || u.role} />
                  {u.id !== user?.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="text-slate-300 hover:text-red-500 transition-colors">
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Backup */}
        <section className="card-soft p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Database size={22} weight="fill" className="text-[#E0890F]" />
            </div>
            <div>
              <h3 className="font-heading font-extrabold text-xl text-[#16205A]">Backup &amp; Export</h3>
              <p className="text-sm text-slate-500">Download a copy of your data any time. Keep a backup somewhere safe.</p>
            </div>
          </div>

          <Button variant="navy" data-testid="export-json-button" onClick={() => download("/export/json")}>
            <DownloadSimple size={16} weight="bold" /> Download Full Backup (JSON)
          </Button>

          <p className="font-semibold text-xs uppercase tracking-wide text-slate-400 mt-6 mb-3">Export a single list (CSV)</p>
          <div className="flex flex-wrap gap-2">
            {collections.map((c) => (
              <button
                key={c}
                data-testid={`export-csv-${c}`}
                onClick={() => download(`/export/csv/${c}`)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg border border-slate-300 px-3.5 py-2 hover:border-[#F49B1C] hover:text-[#E0890F] transition-colors capitalize"
              >
                <FileCsv size={16} weight="fill" /> {c.replace("_", " ")}
              </button>
            ))}
          </div>
        </section>

        {/* Email notifications */}
        <section className="card-soft p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <EnvelopeSimple size={22} weight="fill" className="text-[#3730A3]" />
            </div>
            <div>
              <h3 className="font-heading font-extrabold text-xl text-[#16205A]">Email Notifications</h3>
              <p className="text-sm text-slate-500">Task &amp; follow-up reminders are emailed to every team account when they're due.</p>
            </div>
          </div>

          <div data-testid="notif-status" className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full mb-4 ${notif.configured ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
            <span className={`w-2 h-2 rounded-full ${notif.configured ? "bg-green-500" : "bg-amber-500"}`} />
            {notif.configured ? `Active — sending from ${notif.sender}` : "Not configured yet"}
          </div>

          {notif.configured ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">Send a test email to</label>
                <input
                  data-testid="test-email-input"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="leave blank to use your own email"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F49B1C]/60 focus:border-[#F49B1C]"
                />
              </div>
              <Button variant="navy" data-testid="send-test-email" onClick={sendTest} disabled={sending}>
                <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Sending…" : "Send Test Email"}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              To turn this on, a <span className="font-mono">RESEND_API_KEY</span> and verified sender domain need to be configured on the server. Ask your developer to add them, then this section will let you send a test email.
            </p>
          )}
        </section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Team Account">
        <form onSubmit={save} className="space-y-4">
          <Field label="Full Name *" data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Email *" type="email" data-testid="user-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field label="Password *" type="text" data-testid="user-password-input" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 characters" />
          <Select label="Role" data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="staff">Staff — full access to daily tools</option>
            <option value="admin">Admin — can also manage accounts &amp; backups</option>
            <option value="owner">Owner — full control</option>
          </Select>
          <p className="text-xs text-slate-400">Share these credentials with the person. They can change nothing about your login.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-user-button">Create Account</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
